/**
 * Centralized Error Handling Utility
 * Provides consistent error handling across authentication flows
 */

// Error categories for better organization and handling
export const ErrorCategory = {
  AUTH0: 'AUTH0',
  BACKEND_API: 'BACKEND_API',
  NETWORK: 'NETWORK',
  TOKEN: 'TOKEN',
  PERSONNEL: 'PERSONNEL',
  EMAIL_VERIFICATION: 'EMAIL_VERIFICATION',
  VALIDATION: 'VALIDATION',
  UNKNOWN: 'UNKNOWN',
};

// Error codes that indicate specific scenarios
export const ErrorCode = {
  PERSONNEL_NOT_FOUND: 'PERSONNEL_NOT_FOUND',
  PERSONNEL_RECORD_NOT_FOUND: 'PERSONNEL_RECORD_NOT_FOUND',
  PERSONNEL_CHECK_FAILED: 'PERSONNEL_CHECK_FAILED',
  USER_NOT_FOUND: 'USER_NOT_FOUND',
  EMAIL_NOT_VERIFIED: 'EMAIL_NOT_VERIFIED',
  TOKEN_EXPIRED: 'TOKEN_EXPIRED',
  INVALID_TOKEN: 'INVALID_TOKEN',
  MISSING_REFRESH_TOKEN: 'MISSING_REFRESH_TOKEN',
  SESSION_EXPIRED: 'SESSION_EXPIRED',
  NETWORK_ERROR: 'NETWORK_ERROR',
  TIMEOUT: 'TIMEOUT',
  UNAUTHORIZED: 'UNAUTHORIZED',
  FORBIDDEN: 'FORBIDDEN',
  SERVER_ERROR: 'SERVER_ERROR',
};

// User-friendly error messages
const ERROR_MESSAGES = {
  [ErrorCode.PERSONNEL_NOT_FOUND]: 'Your account is not found in our system. Please contact support for assistance.',
  [ErrorCode.PERSONNEL_RECORD_NOT_FOUND]: 'Your personnel record could not be found. Please contact support.',
  [ErrorCode.PERSONNEL_CHECK_FAILED]: 'Unable to verify your personnel information. Please try again or contact support.',
  [ErrorCode.USER_NOT_FOUND]: 'User account not found. Please sign up first or contact support.',
  [ErrorCode.EMAIL_NOT_VERIFIED]: 'Please verify your email address to continue. Check your inbox for the verification link.',
  [ErrorCode.TOKEN_EXPIRED]: 'Your session has expired. Please log in again.',
  [ErrorCode.INVALID_TOKEN]: 'Invalid authentication token. Please log in again.',
  [ErrorCode.MISSING_REFRESH_TOKEN]: 'Session expired. Please log in again.',
  [ErrorCode.SESSION_EXPIRED]: 'Your session has expired. Please log in again.',
  [ErrorCode.NETWORK_ERROR]: 'Network connection error. Please check your internet connection and try again.',
  [ErrorCode.TIMEOUT]: 'Request timed out. Please try again.',
  [ErrorCode.UNAUTHORIZED]: 'You are not authorized to perform this action. Please log in again.',
  [ErrorCode.FORBIDDEN]: 'Access denied. You do not have permission to access this resource.',
  [ErrorCode.SERVER_ERROR]: 'Server error occurred. Please try again later or contact support if the issue persists.',
  DEFAULT: 'An unexpected error occurred. Please try again.',
};

/**
 * Check if a response indicates an error
 * Handles multiple error formats from different sources (Auth0, Backend API, etc.)
 */
export const isErrorResponse = (response) => {
  if (!response) return true;

  return (
    response.is_error === true ||
    response._http_status >= 400 ||
    (response.code && response.code !== 'SUCCESS') ||
    !!response.error ||
    response.status === 'error'
  );
};

/**
 * Extract error code from various response formats
 */
export const extractErrorCode = (error) => {
  if (!error) return null;

  // Try various paths where error codes might exist
  const possiblePaths = [
    error.code,
    error.error?.code,
    error.context_code,
    error.error?.context_code,
    error.payload?.code,
    error.payload?.error?.code,
    error.payload?.context_code,
    error.response?.data?.error?.code,
    error.response?.data?.code,
    error.error_code,
  ];

  return possiblePaths.find((code) => code) || null;
};

/**
 * Extract error message from various response formats
 */
export const extractErrorMessage = (error) => {
  if (!error) return null;

  // Try various paths where error messages might exist
  const possiblePaths = [
    error.error?.message,
    error.message,
    error.error_description,
    error.description,
    error.response?.data?.error?.message,
    error.response?.data?.message,
    error.payload?.error?.message,
    error.payload?.message,
  ];

  return possiblePaths.find((msg) => msg) || null;
};

/**
 * Extract downstream error details from kvdetail array
 * Some backend errors include nested downstream errors
 */
const extractDownstreamError = (error) => {
  if (!error?.error?.kvdetail || !Array.isArray(error.error.kvdetail)) {
    return null;
  }

  const downstreamBodyEntry = error.error.kvdetail.find(
    (kv) => kv.key === 'downstream_body'
  );

  if (downstreamBodyEntry?.value?.error) {
    return downstreamBodyEntry.value.error;
  }

  return null;
};

/**
 * Categorize error based on error code and content
 */
export const categorizeError = (error) => {
  const errorCode = extractErrorCode(error);
  const errorMessage = extractErrorMessage(error) || '';

  // Check for personnel-related errors
  if (
    errorCode === ErrorCode.PERSONNEL_NOT_FOUND ||
    errorCode === ErrorCode.PERSONNEL_RECORD_NOT_FOUND ||
    errorCode === ErrorCode.PERSONNEL_CHECK_FAILED
  ) {
    return ErrorCategory.PERSONNEL;
  }

  // Check for email verification errors
  if (
    errorCode === ErrorCode.EMAIL_NOT_VERIFIED ||
    (errorMessage.toLowerCase().includes('email') &&
    errorMessage.toLowerCase().includes('verif'))
  ) {
    return ErrorCategory.EMAIL_VERIFICATION;
  }

  // Check for token-related errors
  if (
    errorCode === ErrorCode.TOKEN_EXPIRED ||
    errorCode === ErrorCode.INVALID_TOKEN ||
    errorCode === ErrorCode.MISSING_REFRESH_TOKEN ||
    errorCode === ErrorCode.SESSION_EXPIRED ||
    errorMessage.includes('token') ||
    errorMessage.includes('refresh') ||
    errorMessage.includes('session')
  ) {
    return ErrorCategory.TOKEN;
  }

  // Check for network errors
  if (
    errorCode === ErrorCode.NETWORK_ERROR ||
    errorCode === ErrorCode.TIMEOUT ||
    error.name === 'NetworkError' ||
    error.name === 'TimeoutError' ||
    errorMessage.includes('network') ||
    errorMessage.includes('timeout') ||
    errorMessage.includes('connection')
  ) {
    return ErrorCategory.NETWORK;
  }

  // Check for Auth0 errors
  if (error.error && typeof error.error === 'string' && error.error_description) {
    return ErrorCategory.AUTH0;
  }

  // Check for backend API errors
  if (error.is_error === true || error._http_status || error.context_code) {
    return ErrorCategory.BACKEND_API;
  }

  return ErrorCategory.UNKNOWN;
};

/**
 * Get user-friendly error message
 */
export const getUserFriendlyMessage = (error) => {
  const errorCode = extractErrorCode(error);

  // Check for custom message in error object first
  const customMessage = extractErrorMessage(error);

  // If we have a known error code, use the predefined message
  if (errorCode && ERROR_MESSAGES[errorCode]) {
    return ERROR_MESSAGES[errorCode];
  }

  // If we have a custom message from the backend, use it
  if (customMessage && customMessage.length > 0) {
    return customMessage;
  }

  // Fallback to default message
  return ERROR_MESSAGES.DEFAULT;
};

/**
 * Check if error is a personnel-related error
 */
export const isPersonnelError = (error) => {
  const errorCode = extractErrorCode(error);
  return (
    errorCode === ErrorCode.PERSONNEL_NOT_FOUND ||
    errorCode === ErrorCode.PERSONNEL_RECORD_NOT_FOUND ||
    errorCode === ErrorCode.PERSONNEL_CHECK_FAILED
  );
};

/**
 * Check if error is a token/session error
 */
export const isTokenError = (error) => {
  const errorCode = extractErrorCode(error);
  const errorMessage = extractErrorMessage(error) || '';

  return (
    errorCode === ErrorCode.TOKEN_EXPIRED ||
    errorCode === ErrorCode.INVALID_TOKEN ||
    errorCode === ErrorCode.MISSING_REFRESH_TOKEN ||
    errorCode === ErrorCode.SESSION_EXPIRED ||
    errorMessage.includes('refresh token') ||
    errorMessage.includes('Missing Refresh Token') ||
    error.error === 'missing_refresh_token'
  );
};

/**
 * Check if error is retryable (network issues, timeouts, etc.)
 */
export const isRetryableError = (error) => {
  const category = categorizeError(error);
  const errorCode = extractErrorCode(error);

  return (
    category === ErrorCategory.NETWORK ||
    errorCode === ErrorCode.TIMEOUT ||
    errorCode === ErrorCode.SERVER_ERROR ||
    error._http_status >= 500
  );
};

/**
 * Normalize error response to a consistent format
 */
export const normalizeError = (error) => {
  // If error is null or undefined
  if (!error) {
    return {
      code: null,
      message: ERROR_MESSAGES.DEFAULT,
      category: ErrorCategory.UNKNOWN,
      originalError: null,
      isRetryable: false,
    };
  }

  // Extract error info from response if available
  let errorData = error;
  if (error?.response?.data) {
    errorData = error.response.data;
  } else if (error?.response) {
    errorData = error.response;
  }

  // Check for downstream errors
  const downstreamError = extractDownstreamError(errorData);
  if (downstreamError) {
    errorData = { ...errorData, ...downstreamError };
  }

  const code = extractErrorCode(errorData);
  const message = getUserFriendlyMessage(errorData);
  const category = categorizeError(errorData);
  const isRetryable = isRetryableError(errorData);

  return {
    code,
    message,
    category,
    originalError: error,
    isRetryable,
    isPersonnelError: isPersonnelError(errorData),
    isTokenError: isTokenError(errorData),
  };
};

/**
 * Log error with appropriate level and context
 */
export const logError = (error, context = '') => {
  const normalizedError = normalizeError(error);
  void context;
  // Error logging removed - errors are handled through normalized error object

  return normalizedError;
};

/**
 * Handle async function with automatic error normalization
 * Usage: await handleAsync(asyncFunction, 'context')
 */
export const handleAsync = async (asyncFn, context = '') => {
  try {
    const result = await asyncFn();

    // Check if result itself is an error response
    if (isErrorResponse(result)) {
      const normalizedError = logError(result, context);
      throw normalizedError;
    }

    return result;
  } catch (error) {
    const normalizedError = logError(error, context);
    throw normalizedError;
  }
};

/**
 * Create a retry wrapper for async functions
 * @param {Function} asyncFn - The async function to retry
 * @param {Object} options - Retry options
 * @param {number} options.maxRetries - Maximum number of retries (default: 3)
 * @param {number} options.delayMs - Delay between retries in ms (default: 1000)
 * @param {Function} options.shouldRetry - Custom function to determine if should retry
 * @param {string} options.context - Context for logging
 */
export const withRetry = async (asyncFn, options = {}) => {
  const {
    maxRetries = 3,
    delayMs = 1000,
    shouldRetry = isRetryableError,
  } = options;

  let lastError;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      const result = await asyncFn();

      // Check if result is an error response
      if (isErrorResponse(result)) {
        const normalizedError = normalizeError(result);

        if (shouldRetry(result) && attempt < maxRetries) {
          await new Promise((resolve) => setTimeout(resolve, delayMs * (attempt + 1)));
          lastError = normalizedError;
          continue;
        }

        throw normalizedError;
      }

      return result;
    } catch (error) {
      const normalizedError = normalizeError(error);
      lastError = normalizedError;

      if (shouldRetry(error) && attempt < maxRetries) {
        await new Promise((resolve) => setTimeout(resolve, delayMs * (attempt + 1)));
      } else {
        throw normalizedError;
      }
    }
  }

  throw lastError;
};

const errorHandler = {
  ErrorCategory,
  ErrorCode,
  isErrorResponse,
  extractErrorCode,
  extractErrorMessage,
  categorizeError,
  getUserFriendlyMessage,
  isPersonnelError,
  isTokenError,
  isRetryableError,
  normalizeError,
  logError,
  handleAsync,
  withRetry,
};

export default errorHandler;
